import { Box, Stack, TextField } from '@mui/material';
import React from 'react';
import type { SetupField } from '../../domain/setup/fieldTypes';

const HEX = /^#[0-9a-f]{6}$/i;

export const isHexColour = (value: string) => HEX.test(value.trim());

export function colourError(value: string): string | undefined {
  return value.trim() && !isHexColour(value) ? 'Use a hex colour, e.g. #143c34' : undefined;
}

interface Props {
  field: SetupField;
  value: string;
  error?: string;
  readOnly: boolean;
  markers: React.ReactNode;
  onChange: (value: string) => void;
}

/** A swatch that opens the browser's colour picker, beside the hex value it stores. */
export default function ColourFieldInput({
  field,
  value,
  error,
  readOnly,
  markers,
  onChange,
}: Props) {
  const hex = value.trim();
  const valid = isHexColour(hex);
  const problem = error ?? colourError(hex);
  return (
    <Box sx={{ gridColumn: field.wide ? '1 / -1' : undefined }}>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box
          component="label"
          title={readOnly ? undefined : `Pick ${field.label}`}
          sx={{
            flexShrink: 0,
            width: 40,
            height: 40,
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
            cursor: readOnly ? 'default' : 'pointer',
            bgcolor: valid ? hex : 'background.paper',
            backgroundImage: valid
              ? undefined
              : 'repeating-linear-gradient(45deg, #e0e0e0 0 4px, transparent 4px 8px)',
          }}
        >
          <input
            type="color"
            aria-label={`Pick ${field.label}`}
            value={valid ? hex.toLowerCase() : '#000000'}
            disabled={readOnly}
            onChange={(event) => onChange(event.target.value.toUpperCase())}
            style={{ opacity: 0, width: 0, height: 0, border: 0, padding: 0 }}
          />
        </Box>
        <TextField
          size="small"
          label={field.label}
          required={field.required}
          value={value}
          placeholder="#RRGGBB"
          disabled={readOnly}
          error={Boolean(problem)}
          helperText={problem ?? field.help}
          InputLabelProps={{ shrink: true }}
          onChange={(event) => onChange(event.target.value)}
          sx={{ maxWidth: 180 }}
        />
      </Stack>
      <Box sx={{ mt: 0.5 }}>{markers}</Box>
    </Box>
  );
}
